import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { IssueField, IssueTypeConfig, IssueTypeId } from '@raptor/shared';
import { ISSUE_TYPES } from '@raptor/shared';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { IssueTypeBadge } from '@/components/IssueTypeBadge';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/lib/api/client';

import { useCreateIssue, useProducts } from '../api';
import { buildIssueSchema, type IssueFormValues } from '../lib/issue-schema';
import type { CreateIssueInput } from '../types';
import { AttachmentPicker } from './AttachmentPicker';
import { MarkdownBody } from './MarkdownBody';

const TYPE_ORDER: IssueTypeId[] = ['bug', 'feature', 'enhancement', 'documentation', 'general'];

interface FormShape extends IssueFormValues {
  title: string;
  product: string;
}

// Build an empty form-state object matching the current schema's keys. Keeps
// defaultValues stable per-type so react-hook-form's `reset()` on type-swap
// starts from a known-good baseline.
function emptyValues(type: IssueTypeConfig): FormShape {
  const values: FormShape = { title: '', product: '' };
  for (const f of type.fields) {
    if (f.kind === 'markdown' || !f.id) continue;
    values[f.id] = '';
  }
  return values;
}

export function IssueForm() {
  const navigate = useNavigate();
  const products = useProducts();
  const createIssue = useCreateIssue();

  const [selectedTypeId, setSelectedTypeId] = useState<IssueTypeId>('bug');
  const selectedType = ISSUE_TYPES[selectedTypeId];
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [attachmentsBusy, setAttachmentsBusy] = useState(false);

  const schema = useMemo(() => buildIssueSchema(selectedType), [selectedType]);

  const form = useForm<FormShape>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues(selectedType),
  });

  // Type change: rebuild resolver *and* wipe values so stale keys from the
  // previous type don't linger in form state (they'd be ignored by the schema
  // but would still resurface if the user switched back).
  useEffect(() => {
    form.reset(emptyValues(selectedType));
    // resolver reference is re-derived by useMemo; react-hook-form picks it up
    // via the useForm re-render.
  }, [selectedTypeId, form, selectedType]);

  const productsLoading = products.isLoading;
  const productsError = products.isError;
  const productItems = products.data?.items ?? [];
  const productsEmpty = products.isSuccess && productItems.length === 0;

  const disableSubmit =
    createIssue.isPending || productsLoading || productsError || productsEmpty || attachmentsBusy;

  const onSubmit = form.handleSubmit(async (raw) => {
    // Split fixed fields from dynamic ones — the dynamic ones ship as `values`.
    const { title, product, ...rest } = raw;
    const values: Record<string, string> = {};
    for (const field of selectedType.fields) {
      if (field.kind === 'markdown' || !field.id) continue;
      const v = rest[field.id];
      if (v === undefined || v === null) continue;
      // Optional inputs/textareas: skip empty strings so the composer doesn't
      // emit an empty section. Required fields never reach this branch — zod
      // already rejected them.
      if (!field.required && (typeof v !== 'string' || v.trim() === '')) continue;
      values[field.id] = String(v);
    }

    const envelope: CreateIssueInput = {
      type: selectedTypeId,
      title: String(title).trim(),
      product: String(product),
      values,
      ...(attachmentIds.length > 0 ? { attachmentIds } : {}),
    };

    try {
      const created = await createIssue.mutateAsync(envelope);
      toast.success('Issue submitted');
      navigate(`/issues/${created.id}`);
    } catch (err) {
      handleSubmitError(err, form, selectedType);
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-5" data-testid="issue-new-form">
        {productsError && (
          <Alert variant="destructive">
            <AlertTitle>Couldn&apos;t load products</AlertTitle>
            <AlertDescription>
              We couldn&apos;t fetch the list of products you have access to. Try refreshing.
            </AlertDescription>
          </Alert>
        )}

        {productsEmpty && (
          <Alert variant="destructive" data-testid="issue-new-no-products">
            <AlertTitle>No products assigned</AlertTitle>
            <AlertDescription>
              Your account isn&apos;t linked to any products yet. Contact your administrator to be
              granted access.
            </AlertDescription>
          </Alert>
        )}

        {/* Type chooser is a Select now — the radio-group version cluttered
            the form once the taxonomy widened past two types. Each item is
            rendered as an IssueTypeBadge (icon + clean label from
            ISSUE_TYPES[id].name). Changing the value flows through the same
            useEffect that rebuilds the derived schema and resets field values
            — see the block below that watches selectedTypeId. */}
        <div className="space-y-2">
          <Label htmlFor="issue-new-type">Type</Label>
          <Select value={selectedTypeId} onValueChange={(v) => setSelectedTypeId(v as IssueTypeId)}>
            <SelectTrigger id="issue-new-type" data-testid="issue-new-type" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_ORDER.map((id) => (
                <SelectItem
                  key={id}
                  value={id}
                  data-testid={`issue-new-type-option-${id}`}
                  aria-label={ISSUE_TYPES[id].name}
                >
                  <IssueTypeBadge type={id} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <FormField
          control={form.control}
          name="product"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Product</FormLabel>
              <FormControl>
                <Select
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  disabled={productsLoading || productsError || productsEmpty}
                >
                  <SelectTrigger data-testid="issue-new-product-select">
                    <SelectValue
                      placeholder={productsLoading ? 'Loading products…' : 'Select a product'}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {productItems.map((p) => (
                      <SelectItem
                        key={p.repoName}
                        value={p.repoName}
                        data-testid={`issue-new-product-option-${p.repoName}`}
                      >
                        {p.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <div className="flex items-stretch overflow-hidden rounded-md border border-input">
                  <span
                    className="whitespace-pre bg-slate-100 px-3 py-2 text-sm text-slate-600"
                    data-testid="issue-new-title-prefix"
                  >
                    {selectedType.titlePrefix}
                  </span>
                  <Input
                    className="border-0 focus-visible:ring-0"
                    placeholder="Short summary"
                    data-testid="issue-new-title-input"
                    {...field}
                  />
                </div>
              </FormControl>
              <FormDescription>
                The prefix is added automatically to match Gitea&apos;s convention.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {selectedType.fields.map((f, idx) => (
          <ConfiguredField key={`${selectedTypeId}-${f.id ?? `md-${idx}`}`} field={f} form={form} />
        ))}

        <div className="space-y-2">
          <Label>Attachments</Label>
          <AttachmentPicker
            disabled={createIssue.isPending}
            onChange={setAttachmentIds}
            onBusyChange={setAttachmentsBusy}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="submit"
            data-testid="issue-new-submit"
            disabled={disableSubmit}
            data-busy={attachmentsBusy}
          >
            {createIssue.isPending ? 'Submitting…' : 'Submit'}
          </Button>
        </div>
      </form>
    </Form>
  );
}

// Renders a single field per the config. Split out so the parent stays legible
// and each `render` closure keeps a tight scope.
function ConfiguredField({
  field,
  form,
}: {
  field: IssueField;
  form: ReturnType<typeof useForm<FormShape>>;
}) {
  if (field.kind === 'markdown') {
    return (
      <div
        className="rounded-md border border-slate-200 bg-slate-50 p-3"
        data-testid="issue-new-markdown-intro"
      >
        <MarkdownBody>{field.value ?? ''}</MarkdownBody>
      </div>
    );
  }

  if (!field.id) return null;

  return (
    <FormField
      control={form.control}
      name={field.id}
      render={({ field: rhfField }) => (
        <FormItem>
          <FormLabel>
            {field.label}
            {!field.required && <span className="text-muted-foreground"> (optional)</span>}
          </FormLabel>
          {/* FormControl uses radix Slot and requires a single element child;
              render exactly one branch per field kind rather than a chain of
              conditionals. */}
          {field.kind === 'input' && (
            <FormControl>
              <Input
                placeholder={field.placeholder}
                data-testid={`issue-new-field-${field.id}`}
                value={typeof rhfField.value === 'string' ? rhfField.value : ''}
                onChange={rhfField.onChange}
                onBlur={rhfField.onBlur}
                name={rhfField.name}
                ref={rhfField.ref}
              />
            </FormControl>
          )}
          {field.kind === 'textarea' && (
            <FormControl>
              <Textarea
                rows={5}
                placeholder={field.placeholder}
                data-testid={`issue-new-field-${field.id}`}
                value={typeof rhfField.value === 'string' ? rhfField.value : ''}
                onChange={rhfField.onChange}
                onBlur={rhfField.onBlur}
                name={rhfField.name}
                ref={rhfField.ref}
              />
            </FormControl>
          )}
          {field.kind === 'dropdown' && (
            <Select
              value={typeof rhfField.value === 'string' ? rhfField.value : ''}
              onValueChange={rhfField.onChange}
            >
              <FormControl>
                <SelectTrigger data-testid={`issue-new-field-${field.id}`}>
                  <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {(field.options ?? []).map((opt) => (
                  <SelectItem
                    key={opt}
                    value={opt}
                    data-testid={`issue-new-field-${field.id}-option-${opt}`}
                  >
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {field.description && <FormDescription>{field.description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// Map backend 400 field-detail into per-field react-hook-form errors when
// possible; fall back to a toast for anything else. Keeps the user's input
// intact (react-hook-form's setError is non-destructive).
function handleSubmitError(
  err: unknown,
  form: ReturnType<typeof useForm<FormShape>>,
  type: IssueTypeConfig,
) {
  if (
    err instanceof ApiError &&
    err.status === 400 &&
    err.details &&
    typeof err.details === 'object'
  ) {
    const details = err.details as {
      field?: string;
      message?: string;
      values?: Record<string, string>;
    };
    const fieldId = details.field;
    if (fieldId) {
      const known =
        fieldId === 'title' || fieldId === 'product' || type.fields.some((f) => f.id === fieldId);
      if (known) {
        form.setError(fieldId, {
          type: 'server',
          message: details.message ?? 'Invalid value',
        });
        return;
      }
    }
  }
  toast.error(err instanceof Error ? err.message : 'Failed to submit issue');
}
